import type { Ticker } from 'pixi.js';

export type FrameLimit = 30 | 60 | 120;
export interface DisplaySettings { vsync: boolean; fps: FrameLimit }
const STORAGE_KEY = 'salvatris.display.v1';

/** Browsers synchronize requestAnimationFrame themselves; OFF adds a manual ceiling. */
export class DisplayController {
  private settings: DisplaySettings = { vsync: true, fps: 60 };
  private storage?: Pick<Storage, 'getItem' | 'setItem'>;

  constructor(private readonly ticker: Pick<Ticker, 'maxFPS'>, storage?: Pick<Storage, 'getItem' | 'setItem'>) {
    try {
      this.storage = storage ?? globalThis.localStorage;
      const saved = JSON.parse(this.storage?.getItem(STORAGE_KEY) ?? 'null');
      if (saved && typeof saved.vsync === 'boolean') this.settings.vsync = saved.vsync;
      if (saved && [30, 60, 120].includes(saved.fps)) this.settings.fps = saved.fps;
    } catch { /* Storage may be blocked inside an embedded activity. */ }
    this.apply();
  }
  get value(): Readonly<DisplaySettings> { return { ...this.settings }; }
  get frameLimit(): number { return this.settings.vsync ? 0 : this.settings.fps; }
  setVsync(enabled: boolean): void {
    this.settings.vsync = enabled;
    this.apply();
  }
  setFps(fps: FrameLimit): void {
    if (this.settings.vsync || ![30, 60, 120].includes(fps)) return;
    this.settings.fps = fps;
    this.apply();
  }
  private apply(): void {
    this.ticker.maxFPS = this.frameLimit;
    try { this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.settings)); } catch { /* Session still works. */ }
  }
}

/** A deliberate 30 FPS cap must not trigger the 60 FPS quality thresholds. */
export function resolutionFpsThresholds(limit: number): { low: number; high: number } {
  const target = limit > 0 ? Math.min(60, limit) : 60;
  return { low: target * 50 / 60, high: target * 58 / 60 };
}
